// src/hooks/usePermission.js
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectUserPermissions,
  selectUserRoles,
  selectCurrentUser,
} from '../store/slices/authSlice';
import { getRolesForPath } from '../config/routePermissions';

const SUPER_ADMIN_ALIASES = ['super_admin', 'superadmin', 'super'];
const ORG_ADMIN_ALIASES   = ['org_admin', 'organization_admin', 'orgadmin', 'admin'];

const normaliseOne = (r) => {
  if (!r) return '';
  const raw = typeof r === 'string' ? r : (r.slug || r.name || r.key || '');
  return String(raw).trim().toLowerCase().replace(/[-\s]+/g, '_');
};

const normaliseRoles = (raw) => {
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return list
    .map(normaliseOne)
    .filter(Boolean)
    .map((r) => {
      if (SUPER_ADMIN_ALIASES.includes(r)) return 'super_admin';
      if (ORG_ADMIN_ALIASES.includes(r)) return 'org_admin';
      return r;
    });
};

/* =============================================================
 *  HOOK
 * ============================================================= */
export const usePermission = () => {
  const rawPermissions = useSelector(selectUserPermissions);
  const rawRoles       = useSelector(selectUserRoles);
  const currentUser    = useSelector(selectCurrentUser);

  // Guards — never crash on null / undefined
  const permissions = Array.isArray(rawPermissions) ? rawPermissions : [];

  const roles = useMemo(() => normaliseRoles(rawRoles), [rawRoles]);

  /* -------- isSuperAdmin: flags on user OR role name -------- */
  const isSuperAdmin = useMemo(() => {
    if (currentUser) {
      if (
        currentUser.is_super_admin === true ||
        currentUser.isSuperAdmin === true ||
        currentUser.super_admin === true ||
        currentUser.is_superadmin === true
      ) {
        return true;
      }
    }
    return roles.includes('super_admin');
  }, [currentUser, roles]);

  /* -------- Permission helpers (unchanged API) -------- */
  const hasPermission = (permission) => permissions.includes(permission);

  const hasAllPermissions = (requiredPermissions) =>
    requiredPermissions.every((p) => permissions.includes(p));

  const hasAnyPermission = (requiredPermissions) =>
    requiredPermissions.some((p) => permissions.includes(p));

  /* -------- Role helpers (unchanged API) -------- */
  const hasRole = (role) => roles.includes(role);

  const hasAllRoles = (requiredRoles) =>
    requiredRoles.every((r) => roles.includes(r));

  const hasAnyRole = (requiredRoles) =>
    requiredRoles.some((r) => roles.includes(r));

  const hasPermissionAndRole = (requiredPermissions = [], requiredRoles = []) => {
    const hasPerm = requiredPermissions.length === 0 || hasAnyPermission(requiredPermissions);
    const hasRoleCheck = requiredRoles.length === 0 || hasAnyRole(requiredRoles);
    return hasPerm && hasRoleCheck;
  };

  const canAccessRoles = useCallback(
    (requiredRoles) => {
      if (!requiredRoles || requiredRoles.length === 0) return true;
      if (isSuperAdmin) return true;

      const needed = normaliseRoles(requiredRoles);
      return needed.some((r) => roles.includes(r));
    },
    [roles, isSuperAdmin]
  );

  const canAccessPath = useCallback(
    (pathname) => {
      const required = getRolesForPath(pathname);
      return canAccessRoles(required);
    },
    [canAccessRoles]
  );

  return {
    // existing
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAllRoles,
    hasAnyRole,
    hasPermissionAndRole,

    // new
    isSuperAdmin,
    canAccessRoles,
    canAccessPath,

    // data
    permissions,
    roles,          
    currentUser,
  };
};

export default usePermission;